import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { finalize, map, tap, switchMap, first, shareReplay } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service'
import { AppConfigService } from '../../shared/appconfig/appconfig.service'
import { UserProfile } from '../../shared/models/core.model'
import { MenuService } from '../menu/menu.service'

export { UserProfile } from '../../shared/models/core.model'

// Fail-closed default: empty privileges so the auth guard's privilege check
// fails cleanly until the real profile loads, instead of silently treating
// pre-load state as "Onboarding".
const DEFAULT_PROFILE: UserProfile = {
  firstName: '',
  lastName: '',
  companyName: '',
  phoneNumber: '',
  status: 'profile',
  privileges: [],
  clientJson: '',
  customerId: '',
  paymentMethodId: '',
  email: '',
  country: '',
  postalCode: ''
};

@Injectable({
  providedIn: 'root'
})
export class ProfileService {

  private profileSubject = new BehaviorSubject<UserProfile>({ ...DEFAULT_PROFILE });
  public profile$ = this.profileSubject.asObservable();
  private profileLoaded = false;
  private profileInflight: Observable<UserProfile> | null = null;
  private authStateSub: Subscription | null = null;

  get profile(): UserProfile {
    return this.profileSubject.getValue();
  }

  constructor(
    private http: HttpClient, private authService: AuthService,
    private menuService: MenuService,
    @Inject('BASE_URL') baseUrl: string) {
    // Reset cache on any auth-state change so a same-tab logout/login (or impersonation
    // switch) cannot serve the previous user's profile.
    this.authStateSub = this.authService.authState$.subscribe(() => {
      this.profileLoaded = false;
      this.profileInflight = null;
      this.profileSubject.next({ ...DEFAULT_PROFILE });
    });
  }

  hasPrivilege(priv: string): boolean {
    const p = this.profile;
    if (p && p.privileges !== null && p.privileges.indexOf(priv) >= 0) {
      return true;
    }
    return false;
  }

  hasAnyPrivilege(priv: string[]): boolean {
    for (var i = 0; i < priv.length; i++) {
      if (this.hasPrivilege(priv[i])) {
        return true;
      }
    }
    return false;
  }

  isOnboarding(): boolean {
    const p = this.profile;
    if (p && p.privileges !== null && p.privileges.indexOf('Onboarding') >= 0) {
      return true;
    }
    return false;
  }

  isPending(): boolean {
    const p = this.profile;
    if (p && p.privileges !== null && p.privileges.indexOf('Pending') >= 0) {
      return true;
    }
    return false;
  }

  isBeta(): boolean {
    const p = this.profile;
    if (p && p.privileges !== null && p.privileges.indexOf('Beta') >= 0) {
      return true;
    }
    return false;
  }

  isActive(): boolean {
    const p = this.profile;
    // Case-insensitive on purpose. The API returns whatever _ChatAuth.UserProfiles.Status holds, and
    // the writers disagree: CloudRadial.Auth's ChatAcceptInvitationController creates invited users
    // with status "Active" (capital), while other rows hold "active". A case-sensitive === therefore
    // made every invitation-created user fail this check and take the server-side onboarding path on
    // every login and navigation (CHAT-877). Comparing case-insensitively also fixes the existing
    // rows without a data migration.
    return p?.status?.toLowerCase() === 'active';
  }

  getStatus(): string {
    const p = this.profile;
    if (p && p.status) return p.status;
    return 'profile';
  }

  load(): Observable<UserProfile> {
    if (this.authService.isLoggedIn() === false) {
      return of(this.profile);
    }

    if (this.profileLoaded) {
      return of(this.profile);
    }

    if (this.profileInflight) {
      return this.profileInflight;
    }

    const url = AppConfigService.settings.get('chatstyleapiServer');

    this.profileInflight = this.http.get<UserProfile>(url + 'profile').pipe(
      tap({
        next: (cfg) => {
          this.profileSubject.next(cfg);
        },
        error: (err) => {
          console.error('Error loading profile from API:', err);
        }
      }),
      switchMap(profile =>
        this.menuService.load(profile).pipe(
          tap(() => { this.profileLoaded = true; }),
          map(() => profile)
        )
      ),
      finalize(() => { this.profileInflight = null; }),
      shareReplay(1)
    );
    return this.profileInflight;
  }

  refresh(): Observable<UserProfile> {
    this.profileLoaded = false;
    this.profileInflight = null;
    return this.load();
  }

  save(p: UserProfile): Observable<UserProfile> {
    if (this.authService.isLoggedIn() === false) {
      return of(this.profile);
    }

    const url = AppConfigService.settings.get('chatstyleapiServer');

    return this.http.post<UserProfile>(url + 'profile', p).pipe(
      tap(cfg => {
        this.profileSubject.next(cfg);
      }),
      switchMap(profile =>
        this.menuService.load(profile).pipe(
          tap(() => { this.profileLoaded = true; }),
          map(() => profile)
        )
      )
    );
  }

  update(profile: UserProfile): void {
    this.profileSubject.next(profile);
  }

}
